import { useSetRecoilState } from "recoil";
import styled from "styled-components";
import { Flex, Image } from "rebass/styled-components";
import { memo, useCallback, useEffect, useMemo } from "react";

import Remix from "components/atoms/form/button/remix";
import { ModalType } from "MVVM/@types";
import { ClipReactions } from "MVVM/Components/ClipReactions";
import { AllstarModalState } from "MVVM/State/modals";
import { IClip } from "MVVM/@types";
import { Button, MobileDownload } from "MVVM/Components/Button";
import { RiShareForwardFill } from "react-icons/ri";
import { CopyLink } from "MVVM/Components/Button/Share";
import { More } from "MVVM/Components/Clip/Components/ClipOptions/Components/MoreClipOptionsDropdown";
import { useInteractonInterceptor } from "MVVM/Hooks/useInteractionInterceptor";
import { EPaywallType } from "MVVM/Components/Modals/Paywall";
import { trackClipDownload } from "utils";
import { useUser } from "MVVM/Hooks/useUser";
import { usePortal } from "MVVM/Hooks/usePortal";
import { ClipDownload } from "MVVM/Components/Modals/ClipDownload";
import { QuickEdit } from "MVVM/Components/QuickEdit";

interface IShareSectionProps {
  clip: IClip;
}

const { REACT_APP_S3_CLIP_BUCKET } = process.env;

const ShareSection = memo(({ clip }: IShareSectionProps) => {
  const { allstarUser } = useUser();
  const setAllstarModalState = useSetRecoilState(AllstarModalState);
  const checkFeatureAccessQuery = useInteractonInterceptor();

  const toggleModal = useCallback(
    (modalType: ModalType) => {
      setAllstarModalState({
        data: clip,
        functions: {},
        isOpen: modalType,
      });
    },
    [clip, setAllstarModalState],
  );

  const {
    portalOpen: quickEditPortalOpen,
    setPortalOpen: setQuickEditPortalOpen,
    Portal: QuickEditPortal,
  } = usePortal(
    <QuickEdit
      momentId={clip?.moment?.id}
      closeModal={() => setQuickEditPortalOpen(false)}
    />,
    "root",
  );

  const setReportModalOpen = useCallback(() => {
    setAllstarModalState({
      data: { _id: clip.id, user: { _id: clip.user._id } },
      functions: {},
      isOpen: ModalType.Report,
    });
  }, [clip.id, clip.user._id, setAllstarModalState]);

  const {
    portalOpen: clipDownloadPortalOpen,
    setPortalOpen: setClipDownloadPortalOpen,
    Portal: ClipDownloadPortal,
  } = usePortal(<ClipDownload clip={clip} />, "root");

  const handleDownloadClick = useCallback(async () => {
    const downloadLink = clip.url;

    const { data } = await checkFeatureAccessQuery({
      variables: { feature: "FREE_ENGAGED_DOWNLOAD" },
    });

    if (data.checkFeatureAccess) {
      return setAllstarModalState({
        isOpen: ModalType.Download,
        data: {
          type: EPaywallType.download as EPaywallType,
        },
        functions: {},
      });
    }
    trackClipDownload(clip);
    window.open(downloadLink);
  }, [clip, checkFeatureAccessQuery, setAllstarModalState]);

  const handleQuickEditClick = useCallback(() => {
    setQuickEditPortalOpen(true);
  }, [setQuickEditPortalOpen]);

  const handleClipDownloadClick = useCallback(() => {
    setClipDownloadPortalOpen(true);
  }, [setClipDownloadPortalOpen]);

  const handleShareClick = useCallback(() => {
    // Remove the share query parameter from the URL
    const url = new URL(window.location.href);
    url.searchParams.delete("share");
    window.history.replaceState({}, document.title, url.toString());
    toggleModal(ModalType.Share);
  }, [toggleModal]);

  useEffect(() => {
    if (!clip.status) return;
    const saveQueryParam = new URLSearchParams(window.location.search).get(
      "save",
    );
    const shareQueryParam = new URLSearchParams(window.location.search).get(
      "share",
    );
    if (saveQueryParam) {
      setClipDownloadPortalOpen(true);
    }
    if (shareQueryParam) {
      toggleModal(ModalType.Share);
    }
  }, [clip.status, setClipDownloadPortalOpen, toggleModal]);

  const remixProps = useMemo(
    () => ({
      classification: undefined,
      classificiation: undefined,
      clip,
      disableOnClick: false,
      onClick: handleQuickEditClick,
      setModalOpen: handleQuickEditClick,
    }),
    [clip, handleQuickEditClick],
  );

  const moreProps = useMemo(
    () => ({
      clip,
      setShareModalOpen: handleShareClick,
      setReportModalOpen,
      onDownloadClick: handleDownloadClick,
      position: { top: "-20px", right: "28px" },
    }),
    [clip, handleShareClick, setReportModalOpen, handleDownloadClick],
  );

  return (
    <ShareContainer>
      {clipDownloadPortalOpen && ClipDownloadPortal}
      {quickEditPortalOpen && QuickEditPortal}
      {clip.isRemixable || allstarUser.isAdmin ? (
        <Remix {...remixProps} />
      ) : clip.type === "SQUAD_MONTAGE" ? (
        <Image
          width={125}
          my={4}
          src={`${REACT_APP_S3_CLIP_BUCKET}/static/web/montages/SquadMontage_Horizontal@2x.webp`}
        />
      ) : clip.type === "MATCH_MONTAGE" ? (
        <Image
          width={125}
          my={4}
          src={`${REACT_APP_S3_CLIP_BUCKET}/static/web/montages/MatchMontage_Horizontal@2x.webp`}
        />
      ) : (
        <></>
      )}
      <StyledClipReactions clip={clip} mobileRight="-200px" />
      {allstarUser.loggedIn ? (
        <MobileDownload
          size="social"
          clip={clip}
          clipLink={clip.url}
          onClick={handleClipDownloadClick}
        />
      ) : (
        <CopyLink
          clip={clip}
          clipLink={`${window.location.origin}/clip/${clip.shareId}`}
          onClick={undefined}
          isMontage={false}
          isProfile={false}
        ></CopyLink>
      )}
      <StyledShareButton
        classification="secondaryRounded"
        onClick={handleShareClick}
      >
        <StyledShareIcon size={20} />
      </StyledShareButton>
      <More {...moreProps} />
    </ShareContainer>
  );
});

const ShareContainer = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 25px;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    padding: 0;
    transform: scale(0.9);
  }
`;

const StyledShareButton = styled(Button)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.envy};
  display: flex;

  :hover {
    background-color: ${({ theme }) => theme.colors.darkerEnvy};
  }
`;

const StyledShareIcon = styled(RiShareForwardFill)``;

const StyledClipReactions = styled(ClipReactions)`
  min-height: 40px;
  width: 80px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    height: 36px;
    width: 65px;
  }
`;

export { ShareSection };
